import type { SeatPool, SeatUsageResponse, TenantSeatLimits } from '@bliks-io/contracts';

interface SeatPoolDescriptor {
  /** The pool id `seatPoolsForRoles` reports a user under. */
  pool: SeatPool;
  limitField: keyof TenantSeatLimits;
  usageKey: keyof SeatUsageResponse['data'];
  labelKey: string;
}

/**
 * The seat pools in display order, pairing the cap field a superadmin edits with
 * the key the usage endpoint reports it under. Declared once so the editor and
 * the read-only banners cannot label or pair them differently.
 */
export const SEAT_POOLS: readonly SeatPoolDescriptor[] = [
  {
    pool: 'system_admin',
    limitField: 'maxSystemAdmins',
    usageKey: 'systemAdmins',
    labelKey: 'seatLimits.systemAdmins'
  },
  {
    pool: 'org_manager',
    limitField: 'maxOrgManagers',
    usageKey: 'orgManagers',
    labelKey: 'seatLimits.orgManagers'
  },
  { pool: 'member', limitField: 'maxMembers', usageKey: 'members', labelKey: 'seatLimits.members' }
];

/** `null` is unlimited — rendered as the language-neutral infinity sign. */
export function formatSeatLimit(limit: number | null): string {
  return limit === null ? '∞' : String(limit);
}

export type ParsedSeatLimit = { valid: true; limit: number | null } | { valid: false };

/**
 * Reads one seat-limit input back into the value the PATCH body carries.
 *
 * An emptied input means "unlimited" and becomes an explicit `null`, so callers
 * must feed it the RAW string: a number input reports unparseable text as an
 * empty value, which would turn a typo into a silent "clear this cap".
 *
 * Only plain digits are accepted, not everything `Number` tolerates — "0x10"
 * and "1e3" are typos here, not caps of 16 and 1000.
 */
export function parseSeatLimit(raw: string): ParsedSeatLimit {
  const trimmed = raw.trim();
  if (trimmed === '') return { valid: true, limit: null };
  if (!/^\d+$/.test(trimmed)) return { valid: false };

  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed)) return { valid: false };
  return { valid: true, limit: parsed };
}

/** The stored cap as the input renders it — unlimited (`null`/absent) is empty. */
export function seatLimitToInput(limit: number | null | undefined): string {
  return limit === null || limit === undefined ? '' : String(limit);
}
